import { MouseEvent } from "react"

import { ImageAndIdentifier, Props } from "."

interface Params {
  event: MouseEvent<HTMLButtonElement>
  field: "alternativeText" | "caption"
  image: ImageAndIdentifier
  onRegenerateScreenshotMetadataField?: Props["onRegenerateScreenshotMetadataField"]
  onSetFieldValue: (identifier: string, value: string) => void
  setIsRegenerating: (value: boolean) => void
}

/**
 * regenerateScreenshotMetadata
 *
 * @description Regenerates one screenshot metadata field and applies the value to the current item.
 * @param {Params} params - Regeneration config, handlers, and current image state.
 * @param {MouseEvent<HTMLButtonElement>} params.event - Button click event from the UI action.
 * @param {"alternativeText" | "caption"} params.field - Metadata field to regenerate.
 * @param {ImageAndIdentifier} params.image - Active image item with identifier and URL.
 * @param {Props["onRegenerateScreenshotMetadataField"]} [params.onRegenerateScreenshotMetadataField] - Optional GraphQL regeneration callback.
 * @param {(identifier: string, value: string) => void} params.onSetFieldValue - Setter used to update local image state.
 * @param {(value: boolean) => void} params.setIsRegenerating - Setter used to toggle loading state.
 * @returns {Promise<void>} Resolves after regeneration finishes and state updates are applied.
 *
 */
const regenerateScreenshotMetadata = async ({
  event,
  field,
  image,
  onRegenerateScreenshotMetadataField,
  onSetFieldValue,
  setIsRegenerating
}: Params): Promise<void> => {
  event.stopPropagation()

  if (!onRegenerateScreenshotMetadataField || !image.originalUrl) {
    return
  }

  setIsRegenerating(true)

  try {
    const generated = await onRegenerateScreenshotMetadataField({
      contentId: image.contentId,
      field,
      id: image.id ?? undefined,
      originalUrl: image.originalUrl
    })

    if (generated?.trim()) {
      onSetFieldValue(image.identifier, generated.trim())
    }
  } finally {
    setIsRegenerating(false)
  }
}

export default regenerateScreenshotMetadata
